import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  RefreshCw, Plus, X, Search, ExternalLink, UserCircle, Calendar, Sparkles, Copy, Check, Mail, Link2, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MePilotsTab = () => {
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [syncingBeehiiv, setSyncingBeehiiv] = useState(null);

  const getHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const loadPilots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/me-pilots`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPilots(data.pilots || []);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadPilots(); }, [loadPilots]);

  const searchUsers = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API}/admin/users/search-all?q=${encodeURIComponent(q)}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.users || []);
      }
    } catch (err) { console.error(err); }
    setSearching(false);
  };

  const addExistingUser = async (user) => {
    if (pilots.some(p => p.user_id === user.user_id)) {
      toast.error('Already a /me pilot');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/me-pilots/add`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ user_id: user.user_id })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Added as /me pilot. Token: ${data.magic_link_token}`);
        setSearchQuery(''); setSearchResults([]);
        loadPilots();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const inviteNewUser = async () => {
    if (!newEmail) { toast.error('Email required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/me-pilots/add`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ email: newEmail, name: newName })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Invited ${newEmail} as /me pilot`);
        setNewEmail(''); setNewName(''); setShowInvite(false);
        loadPilots();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const removePilot = async (userId) => {
    if (!window.confirm('Remove this /me pilot?')) return;
    try {
      const res = await fetch(`${API}/admin/me-pilots/${userId}`, { method: 'DELETE', headers: getHeaders() });
      if (res.ok) { toast.success('Removed'); loadPilots(); }
      else toast.error('Failed');
    } catch { toast.error('Failed'); }
  };

  const viewAs = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/me-pilots/${userId}/impersonate`, {
        method: 'POST', headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        const baseUrl = window.location.origin;
        window.open(`${baseUrl}/me?imp_token=${data.session_token}`, '_blank');
      } else toast.error('Failed to create session');
    } catch { toast.error('Failed'); }
  };

  const copyToken = (token) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/auth/magic?token=${token}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        setCopiedToken(token);
        setTimeout(() => setCopiedToken(null), 2000);
        toast.success('Magic link copied!');
      }).catch(() => {
        // Fallback: select text from a temporary input
        const el = document.createElement('textarea');
        el.value = link;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        setCopiedToken(token);
        setTimeout(() => setCopiedToken(null), 2000);
        toast.success('Magic link copied!');
      });
    } else {
      // Fallback for environments without clipboard API
      prompt('Copy this magic link:', link);
    }
  };

  const syncToBeehiiv = async (userId, email) => {
    setSyncingBeehiiv(userId);
    try {
      const res = await fetch(`${API}/admin/users/${userId}/sync-beehiiv`, {
        method: 'POST', headers: getHeaders()
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Synced ${email} to Beehiiv with me_pilot tag`);
        loadPilots();
      } else {
        toast.error(data.message || 'Beehiiv sync failed');
      }
    } catch { toast.error('Beehiiv sync failed'); }
    setSyncingBeehiiv(null);
  };

  return (
    <Card className="shadow-lg border-gray-200">
      <CardContent className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-500" />
            <h3 className="font-semibold text-gray-800">All Pilots (/me)</h3>
            <Badge className="bg-violet-100 text-violet-600">{pilots.length} pilots</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowInvite(!showInvite)} data-testid="invite-me-pilot-btn">
              <Plus className="w-4 h-4 mr-1" /> Invite New
            </Button>
            <Button variant="outline" size="sm" onClick={loadPilots}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {showInvite && (
          <div className="mb-4 p-3 bg-violet-50 rounded-lg border border-violet-200">
            <Label className="text-xs font-semibold text-violet-700 mb-2 block">Invite by Email (creates account + magic link)</Label>
            <div className="flex gap-2">
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name" className="flex-1 text-sm" data-testid="me-pilot-invite-name" />
              <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Email" className="flex-1 text-sm" data-testid="me-pilot-invite-email" />
              <Button size="sm" onClick={inviteNewUser} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="me-pilot-invite-submit">
                {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Send Invite'}
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pilot List */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {pilots.map((pilot) => {
                  const user = pilot.user || {};
                  const displayName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || pilot.user_id;
                  return (
                    <div key={pilot.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`me-pilot-row-${pilot.user_id}`}>
                      <div className="flex items-center gap-3">
                        {user.profile_image || user.avatar ? (
                          <img src={user.profile_image || user.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center">
                            <UserCircle className="w-5 h-5 text-violet-500" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-800">{displayName}</p>
                          <p className="text-[10px] text-gray-400">{user.email}</p>
                        </div>
                        {user.is_demo && <Badge variant="outline" className="text-[9px] h-4">Demo</Badge>}
                        {pilot.pilot_type === "host" && <Badge className="bg-blue-100 text-blue-600 text-[9px] h-4">Host</Badge>}
                        {pilot.pilot_type === "consumer" && <Badge className="bg-violet-100 text-violet-600 text-[9px] h-4">Consumer</Badge>}
                        {pilot.category && <Badge variant="outline" className="text-[9px] h-4 capitalize">{pilot.category}</Badge>}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 flex-wrap justify-end">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{pilot.experience_count || 0} exp</span>
                        <span>{pilot.booking_count || 0} bookings</span>
                        {pilot.magic_link_token && (
                          <Button
                            variant="outline" size="sm"
                            className={`h-7 text-xs gap-1 ${copiedToken === pilot.magic_link_token ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
                            onClick={() => copyToken(pilot.magic_link_token)}
                            data-testid={`copy-link-${pilot.user_id}`}
                          >
                            {copiedToken === pilot.magic_link_token ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />}
                            {copiedToken === pilot.magic_link_token ? 'Copied!' : 'Copy Link'}
                          </Button>
                        )}
                        <Button
                          variant="outline" size="sm"
                          className={`h-7 text-xs gap-1 ${pilot.beehiiv_synced ? 'border-emerald-300 text-emerald-600' : 'border-amber-300 text-amber-600'}`}
                          onClick={() => syncToBeehiiv(pilot.user_id, (pilot.user || {}).email)}
                          disabled={syncingBeehiiv === pilot.user_id}
                          data-testid={`sync-beehiiv-${pilot.user_id}`}
                        >
                          {syncingBeehiiv === pilot.user_id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : pilot.beehiiv_synced ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <Send className="w-3 h-3" />
                          )}
                          {pilot.beehiiv_synced ? 'Synced' : 'Beehiiv'}
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => viewAs(pilot.user_id)} data-testid={`view-as-${pilot.user_id}`}>
                          <ExternalLink className="w-3 h-3" /> View /me
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => removePilot(pilot.user_id)} data-testid={`remove-me-pilot-${pilot.user_id}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {pilots.length === 0 && (
                  <div className="text-center py-10 text-gray-400">
                    <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm font-medium">No /me pilots yet</p>
                    <p className="text-xs">Search existing users or invite new ones</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Search & Add */}
          <div>
            <Label className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
              <Search className="w-3.5 h-3.5" /> Add Existing User
            </Label>
            <p className="text-xs text-gray-400 mb-2">Search by name, email, or company to add as a /me pilot.</p>
            <div className="relative">
              <Input
                value={searchQuery}
                onChange={(e) => searchUsers(e.target.value)}
                placeholder="Search users..."
                className="text-sm"
                data-testid="me-pilot-search"
              />
              {searching && <RefreshCw className="w-3 h-3 animate-spin text-gray-400 absolute right-2 top-1/2 -translate-y-1/2" />}
              {searchResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {searchResults.map((user) => {
                    const alreadyPilot = pilots.some(p => p.user_id === user.user_id);
                    return (
                      <button
                        key={user.user_id} type="button"
                        className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between border-b last:border-b-0 ${alreadyPilot ? 'bg-gray-50 text-gray-400' : 'hover:bg-violet-50'}`}
                        onClick={() => !alreadyPilot && addExistingUser(user)}
                        disabled={saving || alreadyPilot}
                      >
                        <div>
                          <p className="font-medium">{user.name || `${user.first_name || ''} ${user.last_name || ''}`}</p>
                          <p className="text-xs text-gray-400">{user.email}</p>
                        </div>
                        {alreadyPilot ? (
                          <Badge variant="outline" className="text-[10px]">Pilot</Badge>
                        ) : (
                          <Plus className="w-3 h-3 text-violet-500" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-6 p-3 bg-gray-50 rounded-lg">
              <h4 className="text-xs font-semibold text-gray-600 mb-2">How /me Pilots Work</h4>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>- Pilots get full access to the /me consumer experience</li>
                <li>- <strong>Copy Link</strong> - copies their magic login URL to clipboard</li>
                <li>- <strong>Beehiiv</strong> - syncs pilot to Beehiiv with <code className="bg-gray-200 px-1 rounded text-[10px]">me_pilot</code> tag</li>
                <li>- <strong>View /me</strong> - impersonate and preview their dashboard</li>
                <li>- Demo consumer accounts are auto-added as pilots</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default MePilotsTab;
